import { useEffect } from 'react';
import SimpleLayout from '@layout/SimpleLayout/SimpleLayout';
import Head from 'next/head';
import { SWRConfig } from 'swr';
import { Spacings, mediaQuery } from '@styles/index';
import {
  fetchGoalChannelTabs,
  fetchPlaylists,
  fetchSinglePlaylists
} from '@api/fetchers/youtube-course';
import useFullYoutubeChannels from '@api/hooks/youtube-course/useFullYoutubeCourses';
import CoursePage from '@cont/YoutubeCourses/CoursePage';
import { fetchGoalInfo } from '@api/fetchers/goal';
import { youtubeCourseSchema } from '@cont/YoutubeCourses/utils';
import styled from '@emotion/styled';
import { getKey as getTopologyKey } from '@api/hooks/topology/useTopology';
import { segment } from '@actions/analytics';
import useGoalInfo from '@api/hooks/goal/useGoalInfo';
import SEO from '@seo/index';

const Content = styled.div`
  display: flex;
  justify-content: center;
  ${mediaQuery.uptoMobile} {
    display: block;
    margin: 0 ${Spacings.SPACING_4B};
  }
`;

const PageWrapper = styled.div`
  max-width: 1136px;
  display: grid;
  grid-template-columns: repeat(24, 1fr);
  grid-column-gap: 16px;
  ${mediaQuery.aboveTablet} {
    width: 1136px;
  }
`;

const YoutubeCoursePage = ({ goalUID, fallbackData }) => {
  const { keys } = useFullYoutubeChannels(goalUID, fallbackData);
  const metaTitle = `Unacademy - ${fallbackData?.goalData?.name} - Free Courses`;
  const { goalInfo } = useGoalInfo(goalUID);

  useEffect(() => {
    segment.track('YouTube Free Courses - Page View', {
      use_segment_api: true
    });
  }, []);
  return (
    <SWRConfig
      value={{
        revalidateOnMount: false,
        dedupingInterval: 60 * 1000,
        fallback: {
          [keys.goalInfoSWRKey]: fallbackData.goalData,
          [keys.youtubeChannelTabsSWRKey]: fallbackData.channelTabsData,
          [keys.youtubePlaylistsSWRKey]: [fallbackData.playlistsData],
          [keys.singleYoutubePlaylistsSWRKey]: [fallbackData.singlePlaylists]
        }
      }}
    >
      <SEO
        title={metaTitle}
        openGraph={{
          title: metaTitle
        }}
        schema={youtubeCourseSchema({ goalInfo })}
      />
      <Head>
        <title>{metaTitle}</title>
      </Head>
      <Content>
        <PageWrapper>
          <CoursePage goalUID={goalUID} />
        </PageWrapper>
      </Content>
    </SWRConfig>
  );
};

YoutubeCoursePage.pageInfo = {
  name: 'Youtube Explore Page',
  isMWebSupported: true,
  layoutOptions: {
    hideHeader: false,
    hideFooter: false,
    hideFreshbot: true,
    disableDarkMode: true
  }
};

export const getStaticProps = async ({ params }) => {
  try {
    const { goalUID } = params || {};
    if (!goalUID) return { notFound: true };
    // Fetch all the channel details
    const playlistsData = await fetchPlaylists(goalUID);
    const singlePlaylistsData = await fetchSinglePlaylists(goalUID);
    if (!playlistsData?.results?.length)
      return { notFound: true, revalidate: 60 };
    // Fetch all tabs respective to this goal.
    const channelTabsData = await fetchGoalChannelTabs(goalUID);
    // Fetch goal info
    const goalData = await fetchGoalInfo(goalUID);

    return {
      props: {
        fallbackData: {
          channelTabsData,
          playlistsData,
          goalData,
          singlePlaylists: singlePlaylistsData
        },
        goalUID,
        globalFallbackData: {
          [getTopologyKey(goalData?.uid)]: goalData
        }
      },
      revalidate: 600
    };
  } catch {
    return { notFound: true, revalidate: 60 };
  }
};

export const getStaticPaths = async () => {
  return {
    paths: [],
    fallback: 'blocking'
  };
};

const Layout = ({ children }) => (
  <SimpleLayout layoutOptions={{ showLogin: true }}>{children}</SimpleLayout>
);

export default YoutubeCoursePage;
YoutubeCoursePage.Layout = Layout;
YoutubeCoursePage.Wrapper = null;
